import frontMatter from 'front-matter';
import {Query, Config, ErrorFlow} from 'olympe';
import {deleteEmailsFromList, formatEmailList, mergeEmailsList} from '../utils/email';
import UserController from './UserController';
import {PurchaseRequest} from '../models/PurchaseRequest';
import {getValueFromLabelValue} from '../utils/checkObjects';
import {LAMBDA_NAMES, SUPER_USERS_LIST} from '../utils/constants';
import {isRequesterIncomplete, isSpenderIncomplete} from '../utils/checkMandatoryFields';
import PurchaseRequestDM from '../models/cloudobjects/PurchaseRequestDM';

/**
 * Singleton class that control purchase requests.
 */
export class PurchaseRequestController {
    /**
     * Set the default supplier source,
     * checked in oder of preference:
     *  1) if there are any P2P addresses available
     *  2) if there are any PSL addresses available
     *  3) if there are any VMD addresses available
     *
     * @param {*} state
     * @return {Object}
     **/
    static setDefaultSupplierEmails(state) {
        let p2pSupplier = false;
        let pslSupplier = false;
        let vmdSupplier = false;
        if (state.p2pSupplierAddresses.length > 0) {
            p2pSupplier = true;
        } else if (state.vendorContactEmails.length > 0) {
            pslSupplier = true;
        } else if (state.vmdSupplierAddresses.length > 0) {
            vmdSupplier = true;
        }
        return {
            p2pSupplier: p2pSupplier,
            pslSupplier: pslSupplier,
            vmdSupplier: vmdSupplier,
        };
    }

    /**
     * Remove and then add the emails from the different supplier sources in the supplier addresses list
     *
     * @param {*} state
     * @return {string[]}
     **/
    static updateDefaultSupplierEmails(state) {
        let supplierAddresses = state.supplierAddresses;

        // remove first for all 3 sources
        if (!state.p2pSupplier) {
            supplierAddresses = deleteEmailsFromList(supplierAddresses, state.p2pSupplierAddresses);
        }

        if (!state.pslSupplier) {
            supplierAddresses = deleteEmailsFromList(supplierAddresses, state.vendorContactEmails);
        }

        if (!state.vmdSupplier) {
            supplierAddresses = deleteEmailsFromList(supplierAddresses, state.vmdSupplierAddresses);
        }

        // add for the 3 sources
        if (state.p2pSupplier) {
            supplierAddresses = mergeEmailsList(supplierAddresses, state.p2pSupplierAddresses);
        }

        if (state.pslSupplier) {
            supplierAddresses = mergeEmailsList(supplierAddresses, state.vendorContactEmails);
        }

        if (state.vmdSupplier) {
            supplierAddresses = mergeEmailsList(supplierAddresses, state.vmdSupplierAddresses);
        }

        return supplierAddresses;
    }

    /**
     *
     * @param {string} internalComment
     * @param {boolean} sendToSpender
     * @param {Object} spender
     * @param {boolean} sendToRequester
     * @param {Object} requester
     * @param {boolean} sendToBuyer
     * @param {boolean} sendToOther
     * @param {string} otherAddress
     * @param {boolean} sendToSupplier
     * @param {string[]} supplierAddresses
     * @param {string} projectTitle
     * @return {string}
     */
    static addPOSendingEmailsInInternalCommentFromState(
        internalComment,
        sendToSpender,
        spender,
        sendToRequester,
        requester,
        sendToBuyer,
        sendToOther,
        otherAddress,
        sendToSupplier,
        supplierAddresses,
    ) {
        try {
            const frontMatterStartIndex = internalComment.indexOf('---');
            const fixedInternalComment = frontMatterStartIndex > 0 ? internalComment.substring(frontMatterStartIndex) : internalComment;
            const count = (fixedInternalComment.match(/---/g) || []).length;
            const frontMatterString = count > 2 ? fixedInternalComment.substring(0, fixedInternalComment.substring(3).indexOf('---')+1) : fixedInternalComment;
            const content = frontMatter(frontMatterString);

            content.attributes['Requester'] = sendToRequester ? requester.email || `` : '';
            content.attributes['Spender'] = sendToSpender ? spender.email || `` : '';
            content.attributes['Supplier'] = sendToSupplier ? formatEmailList(supplierAddresses) : '';
            content.attributes['Other'] = sendToOther ? `${formatEmailList(otherAddress?.split(';'))}` : '';

            const attributeLines = Object.keys(content.attributes).map((key) => {
                const value = content.attributes[key];
                return `${key}: ${value}`;
            }).filter((v) => v);
            return `Please send the PDF of the PO to:
---
${attributeLines.join('\n')}
---
${content.body}`;
        } catch (e) {
            console.warn(e);
            return `Please send the PDF of the PO to:
---

---`;
        }
    }

    /**
     * Check if this user has access to PR properties
     *
     * @param {Object} authData
     * @param {Object} pr
     * @return {boolean}
     **/
    static userHasAccessToPrData(authData, pr) {
        // Sometimes we get pr as undefined, this will prevent logging error to console.
        if (!pr) return;

        const {headerDetails, spender, requester} = pr;
        const companyCode = headerDetails?.companyCode?.value;
        const plant = headerDetails?.defaultPlant?.value;
        const purchaseOrganization = headerDetails?.purchaseOrganization?.value;
        const purchasingGroup = headerDetails?.purchasingGroup?.value;

        // If we don't have data for the current user, check from user id. Provide access if the user is spender or requester.
        if (UserController.getCurrentUserName() === spender?.value || UserController.getCurrentUserName() === requester?.value) {
            return true;
        }
        const authorizations = authData ?? [];

        /**
         * Check whether at least one authorization entry grants access for a given field.
         * Duplicates in DB are ignored: presence of a matching entry is enough.
         *
         * @param {string} field authorization field to match (company_code, plant, ...)
         * @param {string} value PR value for that field
         * @param {boolean} requireWriteAccess if true, an exact match also needs activity '02'
         * @return {boolean}
         */
        const hasMatchingAuthorization = (field, value, requireWriteAccess = false) => {
            if (!value) {
                return true;
            }
            return authorizations.some((obj) => {
                if (obj[field] === '*') {
                    return true;
                }
                if (obj[field] !== value) {
                    return false;
                }
                return !requireWriteAccess || obj.activity?.includes('02');
            });
        };

        // Check if the values are * (all) or it matches with PR data, and the activity (02 is write access).
        return hasMatchingAuthorization('company_code', companyCode) &&
            hasMatchingAuthorization('plant', plant, true) &&
            hasMatchingAuthorization('purchase_organization', purchaseOrganization, true) &&
            hasMatchingAuthorization('purchasing_group', purchasingGroup, true);
    }

    /**
     * @param {boolean} saved
     * @param {*} spender
     * @param {*} requester
     * @param {LabelValue} wayOfBuying
     * @param {LabelValue} purchasingGroup
     * @param {boolean} areThereMultipleOrderingAddresses
     * @param {boolean} areThereMultipleInvoicePartners
     * @param {LabelValue} orderingAddress
     * @param {LabelValue} invoicePartner
     * @param {[]} materials
     * @param {boolean} showCBJustification
     * @param {[]} cbAttachments
     * @param {string} status
     * @param {LabelValue} companyCode
     * @param {LabelValue} purchaseOrganization
     * @param {LabelValue} defaultPlant
     * @param {*} defaultDeliveryAddress
     * @param {*} defaultRequestedDeliveryDate
     * @param {string} validityStart
     * @param {string} validityEnd
     * @param {[]} accounts
     * @param {string} vendorCode
     * @return {PurchaseRequest.statuses}
     */
    static saveBtnAssignNewStatus(
        saved,
        spender,
        requester,
        wayOfBuying,
        purchasingGroup,
        areThereMultipleOrderingAddresses,
        areThereMultipleInvoicePartners,
        orderingAddress,
        invoicePartner,
        materials,
        showCBJustification,
        cbAttachments,
        status,
        companyCode,
        purchaseOrganization,
        defaultPlant,
        defaultDeliveryAddress,
        defaultRequestedDeliveryDate,
        validityStart,
        validityEnd,
        accounts,
        vendorCode,
    ) {
        const spenderIncomplete = isSpenderIncomplete(
            spender,
            materials,
            accounts,
            companyCode,
            purchaseOrganization,
            defaultPlant,
            defaultDeliveryAddress,
            defaultRequestedDeliveryDate,
            validityStart,
            validityEnd,
            wayOfBuying,
        );

        const requesterIncomplete = isRequesterIncomplete(
            requester,
            wayOfBuying,
            purchasingGroup,
            materials,
            vendorCode,
            companyCode,
        );

        if (saved) {
            if ((status === PurchaseRequest.statuses.pendingRequesterAndSpenderCompletion) ||
                (status === PurchaseRequest.statuses.pendingSpenderCompletion) ||
                (status === PurchaseRequest.statuses.pendingRequesterCompletion) ||
                (status === PurchaseRequest.statuses.pendingRequesterCompletionSaved)) {
                if (spenderIncomplete && requesterIncomplete) {
                    return PurchaseRequest.statuses.pendingRequesterAndSpenderCompletion;
                } else if (spenderIncomplete) {
                    return PurchaseRequest.statuses.pendingSpenderCompletion;
                } else if (requesterIncomplete) {
                    return PurchaseRequest.statuses.pendingRequesterCompletion;
                } else {
                    return PurchaseRequest.statuses.pendingRequesterCompletionSaved;
                }
            } else {
                if (spenderIncomplete && requesterIncomplete) {
                    return PurchaseRequest.statuses.incompleteRequesterAndSpender;
                } else if (spenderIncomplete) {
                    return PurchaseRequest.statuses.incompleteSpender;
                } else if (requesterIncomplete) {
                    return PurchaseRequest.statuses.incompleteRequester;
                } else {
                    return PurchaseRequest.statuses.saved;
                }
            }
        } else {
            if (spenderIncomplete && requesterIncomplete) {
                return PurchaseRequest.statuses.incompleteRequesterAndSpender;
            } else if (spenderIncomplete) {
                return PurchaseRequest.statuses.incompleteSpender;
            } else if (requesterIncomplete) {
                return PurchaseRequest.statuses.incompleteRequester;
            } else {
                return PurchaseRequest.statuses.saved;
            }
        }
    }

    /**
     * @param {BrickContext} $
     * @param {string} prTag
     * @return {Promise<string>}
     */
    static async getUpdatedStatus($, prTag) {
        const prCloudObject = await Query.fromTag(prTag, PurchaseRequestDM).execute();
        const json = prCloudObject.getFirst().get(PurchaseRequestDM.jsonProp);
        const pr = new PurchaseRequest(json);

        return pr.status;
    }

    /**
     * @param {LabelValue} spender
     * @param {LabelValue} requester
     * @param {string} status
     * @return {{saveBtn: boolean, submitBtn: boolean, submitToRequesterBtn: boolean, withdrawnBtn: boolean, rejectBtn: boolean}}
     */
    static getAvailableActions(spender, requester, status) {
        let currentUserId;
        switch (Config.getParameter('ENV')) {
        case 'preprod':
        case 'prod':
            currentUserId = UserController.getCurrentUserName();
            break;
        case 'codev':
        default:
            currentUserId = 'ESDASNI1'; // As there's no current user in dev, we are acting as if we were Nibir
            break;
        }
        const isSpender = getValueFromLabelValue(spender).toUpperCase() === currentUserId.toUpperCase();
        const isRequester = getValueFromLabelValue(requester).toUpperCase() === currentUserId.toUpperCase();
        const isBoth = (isSpender && isRequester) || SUPER_USERS_LIST.includes(currentUserId.toUpperCase());
        const isThirdPerson = !isSpender && !isRequester;
        if (isBoth) {
            if ((status.toLowerCase() === PurchaseRequest.statuses.pendingSB.toLowerCase()) || (status.toLowerCase() === PurchaseRequest.statuses.pendingNbs.toLowerCase())) {
                return {saveBtn: false, submitBtn: false, submitToRequesterBtn: false, withdrawnBtn: true, rejectBtn: false};
            } else {
                return {saveBtn: true, submitBtn: true, submitToRequesterBtn: false, withdrawnBtn: false, rejectBtn: false};
            }
        } else if (isThirdPerson || isRequester) {
            if ((status.toLowerCase() === PurchaseRequest.statuses.created.toLowerCase()) ||
                (status.toLowerCase() === PurchaseRequest.statuses.incompleteSpender.toLowerCase()) ||
                (status.toLowerCase() === PurchaseRequest.statuses.incompleteRequester.toLowerCase()) ||
                (status.toLowerCase() === PurchaseRequest.statuses.incompleteRequesterAndSpender.toLowerCase()) ||
                (status.toLowerCase() === PurchaseRequest.statuses.saved.toLowerCase()) ||
                (status.toLowerCase() === PurchaseRequest.statuses.deniedByRequester.toLowerCase())) {
                return {saveBtn: true, submitBtn: true, submitToRequesterBtn: false, withdrawnBtn: false, rejectBtn: false};
            } else if ((status.toLowerCase() === PurchaseRequest.statuses.pendingSB.toLowerCase()) ||
                (status.toLowerCase() === PurchaseRequest.statuses.pendingNbs.toLowerCase())) {
                return {saveBtn: false, submitBtn: false, submitToRequesterBtn: false, withdrawnBtn: true, rejectBtn: false};
            } else {
                return {saveBtn: true, submitBtn: true, submitToRequesterBtn: false, withdrawnBtn: false, rejectBtn: true};
            }
        } else if (isSpender) {
            if ((status.toLowerCase() === PurchaseRequest.statuses.created.toLowerCase()) ||
                (status.toLowerCase() === PurchaseRequest.statuses.incompleteSpender.toLowerCase()) ||
                (status.toLowerCase() === PurchaseRequest.statuses.incompleteRequesterAndSpender.toLowerCase()) ||
                (status.toLowerCase() === PurchaseRequest.statuses.incompleteRequester.toLowerCase()) ||
                (status.toLowerCase() === PurchaseRequest.statuses.saved.toLowerCase()) ||
                (status.toLowerCase() === PurchaseRequest.statuses.deniedByRequester.toLowerCase())) {
                return {saveBtn: true, submitBtn: false, submitToRequesterBtn: true, withdrawnBtn: false, rejectBtn: false};
            } else {
                return {saveBtn: false, submitBtn: false, submitToRequesterBtn: false, withdrawnBtn: true, rejectBtn: false};
            }
        }
    }

    /**
     * Call the lambda on the PR list that brings in the system alias depending on the CompanyCode
     * @param {BrickContext} $ context to execute the lambda on
     * @param {string} newCompanyCode used to get the system alias
     * @return {Promise<*>}
     */
    static fetchSystemAlias($, newCompanyCode) {
        const getSystemAliasLambda = $.get(LAMBDA_NAMES.GET_SYSTEM_ALIAS);

        const [controlFlowIn, companyCode, userID] = getSystemAliasLambda.getInputs();
        const [controlFlowOut, resultOut, errorOut] = getSystemAliasLambda.getOutputs();

        const getSysAliRunner = $.runner(getSystemAliasLambda)
            .set(companyCode, newCompanyCode)
            .set(userID, UserController.getCurrentUserName('CAADAPAAM'))
            .trigger(controlFlowIn);

        return Promise.any([
            getSysAliRunner.waitFor(controlFlowOut),
            getSysAliRunner.waitFor(errorOut),
        ])
            .then((outputFlow) => {
                if (outputFlow instanceof ErrorFlow) {
                    // TODO Handle more properly the error handling
                    console.error(`An error was encountered while getting the system alias to pick the SAP instance we want to send the request: ${outputFlow.getMessage()}`);
                    return {systemAlias: null};
                } else {
                    return {systemAlias: getSysAliRunner.get(resultOut)};
                }
            })
            .finally(() => getSysAliRunner.destroy());
    }
}

// singleton
export default new PurchaseRequestController();
